// setLivestreamPaymentTimer.js
'use strict';
import { MERGE_OPERATION_DATA } from '../ActionTypes.js';
import clearLivestreamPaymentTimer from '../action/clearLivestreamPaymentTimer.js';
import setIsPrivateStreamUnlocked from '../action/setIsPrivateStreamUnlocked.js';
import getTimestampOffset, {
  Accuracy,
} from '../selector/getTimestampOffset.js';
import getStreamTokenDecodedData from '../selector/getStreamTokenDecodedData.js';
import getOperationData from '../selector/getOperationData.js';

import getCurrentUnixTimestamp from '../resource/getCurrentUnixTimestamp.js';

/**
 * Set livestream payment timer
 * @kind action
 * @param {string} {streamId} - stream id.
 * @param {number} {paymentTimestamp} - submit payment unix timestamp.
 * @param {string} {via} - submit payment via action.
 * @return {Promise} Action promise.
 */
const setLivestreamPaymentTimer =
  ({ streamId, paymentTimestamp, via } = {}) =>
  async (dispatch, getState) => {
    const activeStreamId = getOperationData(
      getState(),
      ['liveStream'],
      'activeStreamId'
    );
    if (
      !activeStreamId ||
      !streamId ||
      !paymentTimestamp ||
      streamId !== activeStreamId
    )
      return dispatch({ type: '' });
    dispatch(
      setIsPrivateStreamUnlocked({
        streamId,
        isPrivateStreamUnlocked: true,
      })
    );
    dispatch(clearLivestreamPaymentTimer({ streamId }));
    const now = getCurrentUnixTimestamp({
      offsetSeconds: getTimestampOffset(getState(), Accuracy.SECOND),
    });
    // if user can execute `submitStreamPayment` now, then there is no need to put `submitStreamPayment` into timeout callback.
    // this also prevent to encounter `timerExecuteNow > tokenExpiredUnixTimestamp` in timeout callback.
    // which happens on tab restored from background.
    if (now >= paymentTimestamp) {
      const submitStreamPayment = (await import('./submitStreamPayment.js'))
        .default;
      return dispatch(submitStreamPayment({ streamId, via }));
    }
    const timerId = setTimeout(
      async () => {
        // Check execute time to confirm timer trigger expected
        // E.g. App change from background to foreground
        dispatch({
          type: MERGE_OPERATION_DATA,
          payload: {
            selectPath: ['timer', 'livestream', streamId, 'payment'],
            data: {
              timerId: undefined,
              unixTimestamp: undefined,
            },
          },
        });
        const timerExecuteNow = getCurrentUnixTimestamp({
          offsetSeconds: getTimestampOffset(getState(), Accuracy.SECOND),
        });
        const tokenExpiredUnixTimestamp = getStreamTokenDecodedData(
          getState(),
          streamId
        )?.exp;
        if (timerExecuteNow > tokenExpiredUnixTimestamp) {
          dispatch({ type: '' });
        } else {
          const submitStreamPayment = (await import('./submitStreamPayment.js'))
            .default;
          dispatch(submitStreamPayment({ streamId, via }));
        }
      },
      (paymentTimestamp - now) * 1000
    );
    dispatch({
      type: MERGE_OPERATION_DATA,
      payload: {
        selectPath: ['timer', 'livestream', streamId, 'payment'],
        data: {
          timerId,
          unixTimestamp: paymentTimestamp,
        },
      },
    });
  };

export default setLivestreamPaymentTimer;
